import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import BorderedInput from '@navi/web-ui/lib/primitives/BorderedInput/BorderedInput';
import Button from '@navi/web-ui/lib/primitives/Button/index';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Dropdown from 'src/components/Dropdown';
import Loader from 'src/components/Loader/Loader';
import { SelectPickerOptionProps } from 'src/components/interfaces';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import {
  generatePaymentLink,
  getForeclosureAmount,
  sendPaymentLink
} from '../../actions/paymentsActions';
import styles from './index.module.scss';
import { PaymentPayload, PaymentType } from '../../interfaces/Payments.type';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '../../../../components/TooltipV2/TooltipV2';
import { PhoneNumberSourceText } from 'src/components/Ameyo/interfaces';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import ForeclosureDetails from './ForeclosureDetails';
import Modal from '@cp/src/components/Modal/Modal';
import { DateFormat, getFormatDate, utcToIST } from '@cp/src/utils/DateHelper';
import FutureForeClosureModal from './FutureForeClosureModal';

interface IPaymentsFormValues {
  alternateContactNumber: string;
  numberCustomId?: string;
  amount?: string;
}

const PAYMENT_OPTIONS = [
  { value: 'manualRepayment', label: 'Manual Repayment', isDisabled: false },
  { value: 'foreclosure', label: 'Foreclosure', isDisabled: false, source: '' }
] as SelectPickerOptionProps[];

const Payments = () => {
  const { loanId = '', customerId = '' } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<IPaymentsFormValues>();
  const telephones = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]?.telephonesv2
  );
  const outstandingAmount = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.details?.data?.outstandingAmount
        ?.amount || ''
  );
  const editAccessFlag = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details?.data?.editAccessFlag
  );
  const pauseEfforts = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.pause?.data?.isPaused
  );

  const shouldDisableCTA = () => {
    if (isGlobalAccessGiven) {
      return !editAccessFlag;
    }

    return pauseEfforts ? true : false;
  };

  const user = useSelector((state: RootState) => state?.common?.userData);
  const isGlobalAccessGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const disableCTAs = shouldDisableCTA();
  const [copyingLink, setCopyingLink] = useState<boolean>(false);
  const [sendLink, setSendLink] = useState<boolean>(false);
  const [foreclosureAmount, setForeclosureAmount] = useState(null);
  const [foreclosureAmountLoading, setForeclosureAmountLoading] = useState(false);
  const [showForeclosureDetailsModal, setShowForeclosureDetailsModal] = useState(false);
  const selectedTelephone = watch('alternateContactNumber');
  const formDataAmount = watch('amount');

  useEffect(() => {
    getForeclosureAmount(loanId, getFormatDate(utcToIST(new Date()), DateFormat.YYYY_MM_DD)).then(
      res => {
        setForeclosureAmount(res?.data);
      }
    );
  }, []);

  const displaySourceText = (sources: string[]) => {
    if (sources?.length === 0) {
      return <span className={styles.sourceTextNames}>{'No sources'}</span>;
    }
    if (sources?.length === 1) {
      return <span className={styles.sourceTextNames}>{'(' + sources[0] + ')'}</span>;
    }
    if (sources?.length === 2) {
      return (
        <span className={styles.sourceTextNames}>{'(' + sources[0] + ', ' + sources[1] + ')'}</span>
      );
    }
    if (sources?.length > 2) {
      return <span className={styles.sourceTextNames}>{'(' + sources[0] + ', ' + sources[1]}</span>;
    }
  };
  const telephoneList = useMemo(
    () =>
      telephones?.data?.map(tel => ({
        value: tel.number,
        label: tel.number,
        numberCustomId: tel.numberCustomId,
        source: (
          <Typography className={styles.sourceText} variant="p3">
            {displaySourceText(tel?.sources)}
            {tel.sources.length > 2 && (
              <>
                T
                <Tooltip>
                  <TooltipTrigger>
                    <span className={styles.sourceTextExtras}>
                      {' +' + (tel.sources.length - 2) + ' more'}
                    </span>
                    {')'}
                  </TooltipTrigger>

                  <TooltipContent className={styles.tooltip}>
                    {tel.sources.slice(2, tel.sources.length).map((i, index) => (
                      <>
                        <span>
                          {PhoneNumberSourceText[i] ?? i}{' '}
                          {index < tel.sources.length - 3 ? ', ' : ''}
                        </span>
                      </>
                    ))}
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </Typography>
        )
      })) || [],
    [telephones?.data]
  );

  const getSelectedSourceLabels = () => {
    const selectedSourceLabels = telephones?.data
      ?.filter(t => {
        return selectedTelephone?.includes(t.number as string);
      })
      ?.map(t => t.sources);
    return selectedSourceLabels?.[0]?.join(', ');
  };

  const handleTelephoneChange = (option: SelectPickerOptionProps) => {
    setValue('alternateContactNumber', option.value as string);
    setValue('numberCustomId', option.numberCustomId);
  };

  const selectDropdown = (
    <>
      <Dropdown
        {...register('alternateContactNumber', { required: true })}
        options={telephoneList.slice(0, 1)}
        onSelectionChange={handleTelephoneChange}
        containerClasses={styles.dropdown}
        pickerContainerClasses="rr-block"
        innerContainerStyle="rr-block"
        placeholder="Choose a contact"
        defaultSelected={telephoneList[0]}
        multiSelect={false}
        showSourceAtBottom={true}
        selectedLabels={getSelectedSourceLabels()}
      />
    </>
  );

  const [selectedPaymentValue, setSelectedPaymentValue] = useState(PAYMENT_OPTIONS[0]);

  const paymentDropdownHandler = (action: SelectPickerOptionProps) => {
    setSelectedPaymentValue(action);
  };

  useEffect(() => {
    if (telephones) {
      setValue('alternateContactNumber', telephones?.data?.[0]?.number || '');
      setValue('numberCustomId', telephones?.data?.[0]?.numberCustomId || '');
    }
    setValue('amount', String(outstandingAmount) || '');
  }, [telephones, outstandingAmount]);

  const generatePayload = (
    formData: IPaymentsFormValues,
    isForeclosure?: boolean
  ): PaymentPayload => {
    const { alternateContactNumber, numberCustomId, amount } = formData;
    const payload = {
      type: isForeclosure ? PaymentType.FORECLOSURE : PaymentType.CUSTOM,
      phoneNumber: alternateContactNumber,
      phoneNumberCustomId: numberCustomId,
      customAmount: amount,
      loanAccountNumber: loanId
    };
    if (isForeclosure) {
      delete payload.customAmount;
    }
    return payload;
  };

  const handleCopyLink = (formData: IPaymentsFormValues) => {
    setCopyingLink(true);
    const payload = generatePayload(formData);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Payments_CopyPaymentLink, {
      ...payload
    });
    generatePaymentLink(loanId, customerId, payload, setCopyingLink);
  };

  const handleSendLink = (formData: IPaymentsFormValues) => {
    if (disableCTAs) return;
    setSendLink(true);
    const payload = generatePayload(formData);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Payments_SharePaymentLink, {
      ...payload
    });
    sendPaymentLink(loanId, customerId, payload, setSendLink);
  };

  const handleCopyForeclosureLink = (formData: IPaymentsFormValues) => {
    if (disableCTAs) return;
    setCopyingLink(true);
    const payload = generatePayload(formData, true);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Payments_SharePaymentLink, {
      ...payload
    });
    generatePaymentLink(loanId, customerId, payload, setCopyingLink);
  };

  const openModal = useCallback(() => {
    setShowForeclosureDetailsModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowForeclosureDetailsModal(false);
  }, []);

  return (
    <div key={createKey(loanId, customerId)} className={styles.paymentLinkContainer}>
      <form>
        <div className={styles.dropdownContainer}>
          <Typography variant={'p4'} as={'p'}>
            Action
            {
              <sup>
                <RequiredIcon className={styles.requiredIcon} />
              </sup>
            }
          </Typography>
          <Dropdown
            defaultSelected={PAYMENT_OPTIONS[0]}
            options={PAYMENT_OPTIONS}
            onSelectionChange={paymentDropdownHandler}
            placeholder="Select payment type"
            containerClasses={styles.dropdown}
          />
        </div>
        {selectedPaymentValue?.value === 'manualRepayment' ? (
          <div>
            <div className={styles.lables}>
              <Typography as={'p'} className={styles.lightText} variant={'p4'}>
                Select number
              </Typography>
            </div>
            {selectDropdown}
            <div className={styles.inputContainer}>
              <BorderedInput
                inputLabel={'Enter amount'}
                value={outstandingAmount}
                containerClassName={styles.amountInput}
                LeftInputAdornment="₹"
                type={'number'}
                error={
                  errors?.amount ? errors?.['amount']?.message || 'Please enter a valid amount' : ''
                }
                {...register('amount', {
                  required: true,
                  min: { value: 1, message: 'Please enter a greater value' },
                  validate: value => {
                    const amountVal = Number(value);
                    if (
                      amountVal === foreclosureAmount?.totalAmount ||
                      amountVal === outstandingAmount
                    )
                      return true;
                    return Number.isInteger(amountVal);
                  }
                })}
              />
            </div>
            {Number(formDataAmount) === foreclosureAmount?.totalAmount ? (
              <Typography variant="h5" color="var(--navi-color-green-base)">
                Foreclosure amount
              </Typography>
            ) : null}
            <div className={styles.buttonContainer}>
              <Button
                onClick={handleSubmit(handleCopyLink)}
                name={'link_copy'}
                type={'submit'}
                variant={'secondary'}
                disabled={copyingLink || disableCTAs}
                className="flex-1 py-2"
                size="small"
              >
                Copy payment link
              </Button>
              <Button
                onClick={handleSubmit(handleSendLink)}
                name={'link_generate'}
                type={'submit'}
                variant={'primary'}
                disabled={disableCTAs || sendLink}
                className="flex-1 py-2"
                size="small"
              >
                Share payment link
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <ForeclosureDetails
              foreclosureAmount={foreclosureAmount}
              isLoading={foreclosureAmountLoading}
            />
            <div className={styles.foreclosureButtonContainer}>
              <Button
                onClick={handleSubmit(handleCopyForeclosureLink)}
                name={'link_copy'}
                type={'submit'}
                variant={'primary'}
                disabled={disableCTAs}
                fullWidth
              >
                {copyingLink ? 'Copying foreclosure payment link' : 'Copy foreclosure payment link'}
              </Button>
              <Button onClick={handleSubmit(openModal)} variant={'text'} fullWidth className="p-4">
                Check Foreclosure amount by date
              </Button>
              <Modal
                visible={showForeclosureDetailsModal}
                onClose={closeModal}
                header={'Calculate Foreclosure amount'}
                headerClassName="!pr-[18px] !pb-3"
                contentWrapperClass="!ml-0"
                closeOnOutsideClick
              >
                <FutureForeClosureModal />
              </Modal>
            </div>
          </div>
        )}
      </form>
      <Loader show={sendLink || copyingLink} className={styles.loadingState} animate={false} />
    </div>
  );
};

export default React.memo(Payments);
